import { html, LitElement, nothing, render, type TemplateResult } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';

import {
    initialValues,
    letterToText,
    missingRequired,
    renderLetter,
    type LegalLetterDefinition,
    type LegalLetterField,
    type LegalLetterValues,
    type RenderedBlock,
    type RenderedLetter,
} from './LegalLetterDocument.js';

interface LegalLetterMessages {
    chooseLetter: string;
    formHeader: string;
    previewHeader: string;
    previewNote: string;
    required: string;
    missing: string;
    copy: string;
    copied: string;
    copyFailed: string;
    print: string;
    printHint: string;
    clear: string;
    cleared: string;
    /** Confirmation before copying or printing a letter with required fields left empty; `{fields}` lists them. */
    confirmIncomplete: string;
}

const PRINTING_CLASS = 'legal-letter-printing';

/**
 * Fillable letter templates on prawkostart.pl's guides (<legal-letter>): one
 * component for every letter - each letter is pure data from LD (fields and a
 * document template, see LegalLetterDocument.ts). Form on one side, live
 * preview of the letter on the other, plus copy, print (A4 / "Save as PDF" in
 * the browser's print dialog) and clear.
 *
 * Privacy: what the user types lives only in this instance's @state, for the
 * lifetime of the page. Nothing goes to localStorage/cookies, a server or
 * analytics - a reload or "clear" erases it. The privacy policy and the note
 * under the form promise exactly that; keep it that way.
 */
@customElement('legal-letter')
export class LegalLetter extends LitElement {
    protected createRenderRoot(): HTMLElement {
        return this;
    }

    @property({ type: Object }) messages?: LegalLetterMessages;
    @property({ type: Array }) letters: LegalLetterDefinition[] = [];
    /** Id of the letter shown first. */
    @property() initial?: string;

    @state() private selectedId?: string;
    @state() private values: Record<string, LegalLetterValues> = {};
    @state() private status = '';

    private get letter(): LegalLetterDefinition | undefined {
        const id = this.selectedId ?? this.initial;
        return this.letters.find((letter) => letter.id === id) ?? this.letters[0];
    }

    private valuesFor(letter: LegalLetterDefinition): LegalLetterValues {
        return this.values[letter.id] ?? initialValues(letter);
    }

    private setValue(letter: LegalLetterDefinition, name: string, value: string | boolean): void {
        this.values = { ...this.values, [letter.id]: { ...this.valuesFor(letter), [name]: value } };
        this.status = '';
    }

    private fieldId(name: string): string {
        return `${this.id || 'legal-letter'}-${this.letter!.id}-${name}`;
    }

    // Copying or printing an incomplete letter is allowed (the gaps are dotted
    // out and can be filled in by hand), but only after the user confirms it.
    private confirmComplete(missing: LegalLetterField[]): boolean {
        if (missing.length === 0) return true;
        const fields = missing.map((field) => field.label).join(', ');
        return window.confirm(this.messages!.confirmIncomplete.replace('{fields}', fields));
    }

    private async copy(rendered: RenderedLetter, missing: LegalLetterField[]): Promise<void> {
        if (!this.confirmComplete(missing)) return;
        try {
            await navigator.clipboard.writeText(letterToText(rendered));
            this.status = this.messages!.copied;
        } catch {
            // No clipboard (insecure context, denied permission): the preview
            // text is still selectable.
            this.status = this.messages!.copyFailed;
        }
    }

    // Prints a copy of the sheet placed directly in <body>, with everything
    // else hidden by the print stylesheet (_legal-letter.scss) - the page
    // layout around the article never reaches the paper.
    private print(rendered: RenderedLetter, missing: LegalLetterField[]): void {
        if (!this.confirmComplete(missing)) return;
        // A copy left behind when the browser never fired afterprint (some
        // mobile browsers) would be printed a second time.
        for (const stale of document.querySelectorAll('body > .legal-letter-print-root')) stale.remove();
        const root = document.createElement('div');
        root.className = 'legal-letter-print-root';
        render(this.renderSheet(rendered), root);
        document.body.append(root);
        document.documentElement.classList.add(PRINTING_CLASS);

        const cleanup = () => {
            root.remove();
            document.documentElement.classList.remove(PRINTING_CLASS);
            window.removeEventListener('afterprint', cleanup);
        };
        window.addEventListener('afterprint', cleanup);
        this.status = this.messages!.printHint;
        window.print();
    }

    private clear(): void {
        this.values = {};
        this.status = this.messages!.cleared;
    }

    render() {
        const letter = this.letter;
        if (!this.messages || !letter) return nothing;
        const values = this.valuesFor(letter);
        const rendered = renderLetter(letter, values);
        const missing = missingRequired(letter, values);

        return html`
            ${this.letters.length > 1 ? this.renderChoice(letter) : nothing}
            <p class="legal-letter__description">${letter.description}</p>

            <div class="legal-letter__layout">
                <form class="legal-letter__form" @submit=${(event: Event) => event.preventDefault()}>
                    <h3 class="legal-letter__header">${this.messages.formHeader}</h3>
                    ${letter.fields.map((field) => this.renderField(letter, field, values[field.name]))}
                </form>

                <section class="legal-letter__preview" aria-labelledby=${this.fieldId('preview')}>
                    <h3 class="legal-letter__header" id=${this.fieldId('preview')}>${this.messages.previewHeader}</h3>
                    <p class="legal-letter__note">${this.messages.previewNote}</p>
                    ${this.renderSheet(rendered)}
                </section>
            </div>

            ${
                missing.length > 0
                    ? html`<p class="legal-letter__missing">
                      ${this.messages.missing} ${missing.map((field) => field.label).join(', ')}.
                  </p>`
                    : nothing
            }

            <div class="legal-letter__actions">
                <button type="button" class="legal-letter__button" @click=${() => this.copy(rendered, missing)}>
                    ${this.messages.copy}
                </button>
                <button
                    type="button"
                    class="legal-letter__button legal-letter__button--primary"
                    @click=${() => this.print(rendered, missing)}
                >
                    ${this.messages.print}
                </button>
                <button
                    type="button"
                    class="legal-letter__button legal-letter__button--secondary"
                    @click=${() => this.clear()}
                >
                    ${this.messages.clear}
                </button>
            </div>
            <p class="legal-letter__status" role="status" aria-live="polite">${this.status}</p>
        `;
    }

    private renderChoice(current: LegalLetterDefinition) {
        return html`
            <fieldset class="legal-letter__choice">
                <legend>${this.messages!.chooseLetter}</legend>
                <div class="legal-letter__options">
                    ${this.letters.map(
                        (letter) => html`
                            <button
                                type="button"
                                class="legal-letter__option ${
                                    letter.id === current.id ? 'legal-letter__option--selected' : ''
                                }"
                                aria-pressed=${letter.id === current.id}
                                @click=${() => {
                                    this.selectedId = letter.id;
                                    this.status = '';
                                }}
                            >
                                ${letter.label}
                            </button>
                        `,
                    )}
                </div>
            </fieldset>
        `;
    }

    private renderField(letter: LegalLetterDefinition, field: LegalLetterField, value: string | boolean) {
        const id = this.fieldId(field.name);
        const hintId = field.hint ? `${id}-hint` : undefined;
        const hint = field.hint ? html`<span class="legal-letter__hint" id=${hintId!}>${field.hint}</span>` : nothing;
        const onInput = (event: Event) => {
            const input = event.target as HTMLInputElement | HTMLTextAreaElement;
            this.setValue(letter, field.name, input.value);
        };

        if (field.type === 'checkbox') {
            return html`
                <div class="legal-letter__field legal-letter__field--checkbox">
                    <input
                        type="checkbox"
                        id=${id}
                        .checked=${value === true}
                        aria-describedby=${hintId ?? nothing}
                        @change=${(event: Event) =>
                            this.setValue(letter, field.name, (event.target as HTMLInputElement).checked)}
                    />
                    <label for=${id}>${field.label}</label>
                    ${hint}
                </div>
            `;
        }

        const label = html`<label for=${id}>
            ${field.label}
            ${field.required ? html`<span class="legal-letter__required">(${this.messages!.required})</span>` : nothing}
        </label>`;

        const control: TemplateResult =
            field.type === 'textarea'
                ? html`<textarea
                      id=${id}
                      rows="4"
                      .value=${String(value)}
                      placeholder=${field.placeholder ?? nothing}
                      ?required=${field.required}
                      aria-describedby=${hintId ?? nothing}
                      @input=${onInput}
                  ></textarea>`
                : html`<input
                      type=${field.type}
                      id=${id}
                      .value=${String(value)}
                      placeholder=${field.placeholder ?? nothing}
                      autocomplete=${field.autocomplete ?? 'off'}
                      ?required=${field.required}
                      aria-describedby=${hintId ?? nothing}
                      @input=${onInput}
                  />`;

        return html`<div class="legal-letter__field">${label} ${control} ${hint}</div>`;
    }

    private renderBlock(block: RenderedBlock) {
        return block.kind === 'list'
            ? html`<ul class="legal-letter__list">
                  ${block.items.map((item) => html`<li>${item}</li>`)}
              </ul>`
            : block.kind === 'heading'
              ? html`<p class="legal-letter__heading">${block.text}</p>`
              : html`<p>${block.text}</p>`;
    }

    // The last paragraph, the closing and the signature are one unbreakable
    // block (.legal-letter__sign-off), so a page break never leaves the
    // signature alone at the top of the next page.
    private renderSheet(letter: RenderedLetter) {
        const body = letter.body.slice(0, -1);
        const lastBlock = letter.body.at(-1);
        return html`
            <article class="legal-letter__sheet" lang=${letter.language}>
                <div class="legal-letter__top">
                    <address class="legal-letter__sender">
                        ${letter.sender.map((line) => html`<span>${line}</span>`)}
                    </address>
                    <p class="legal-letter__place-date">${letter.placeDate}</p>
                </div>
                <address class="legal-letter__recipient">
                    ${letter.recipient.map((line) => html`<span>${line}</span>`)}
                </address>
                <p class="legal-letter__subject">${letter.subject}</p>
                ${body.map((block) => this.renderBlock(block))}
                <div class="legal-letter__sign-off">
                    ${lastBlock ? this.renderBlock(lastBlock) : nothing}
                    <p class="legal-letter__closing">${letter.closing}</p>
                    <div class="legal-letter__signature">
                        <span class="legal-letter__signature-line"></span>
                        <span>${letter.signature}</span>
                        <span class="legal-letter__signature-caption">${letter.signatureCaption}</span>
                    </div>
                </div>
                ${
                    letter.attachments.length > 0
                        ? html`<div class="legal-letter__attachments">
                          <p>${letter.attachmentsHeader}</p>
                          <ol>
                              ${letter.attachments.map((item) => html`<li>${item}</li>`)}
                          </ol>
                      </div>`
                        : nothing
                }
            </article>
        `;
    }
}

declare global {
    interface HTMLElementTagNameMap {
        'legal-letter': LegalLetter;
    }
}
